import { useState, useMemo } from 'react';
import { CheckCircle2, XCircle, Search, AlertTriangle, Clock, Battery, Wifi, DollarSign, Eye } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { AdminTabShell } from './AdminTabShell';
import {
  type ReportReadinessRow,
  type BlockedReason,
  useAdminReportReadiness,
} from '@/hooks/admin/useAdminReportReadiness';

// ─── Labels ─────────────────────────────────────────────────────────────

const BLOCKED_REASON_LABELS: Record<string, string> = {
  not_subscribed: 'Not Subscribed',
  subscription_disabled: 'Subscription Disabled',
  missing_battery_snapshot: 'Missing Battery Snapshot',
  low_battery_topology_confidence: 'Low Topology Confidence',
  topology_review_required: 'Topology Review Required',
  insufficient_telemetry: 'Insufficient Telemetry',
  missing_electricity_rate: 'Missing Electricity Rate',
};

const BLOCKED_REASON_ICONS: Record<string, typeof AlertTriangle> = {
  not_subscribed: XCircle,
  subscription_disabled: XCircle,
  missing_battery_snapshot: Battery,
  low_battery_topology_confidence: Eye,
  topology_review_required: Eye,
  insufficient_telemetry: Wifi,
  missing_electricity_rate: DollarSign,
};

// ─── Filter ─────────────────────────────────────────────────────────────

type ReadinessFilter = 'all' | 'subscribed' | 'ready' | 'blocked' | 'not_subscribed';

// ─── Detail panel ───────────────────────────────────────────────────────

function ReadinessDetailPanel({
  row,
  onClose,
}: {
  row: ReportReadinessRow;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-y-0 right-0 w-full max-w-md bg-wt-bg-primary border-l border-wt-border shadow-xl z-50 overflow-y-auto">
      <div className="p-5 space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-sm font-semibold text-wt-text-primary">{row.property_name}</h2>
            <p className="text-xs text-wt-text-muted mt-0.5 font-mono">
              {row.provider_type} &middot; {row.system_count} system{row.system_count !== 1 ? 's' : ''}
            </p>
          </div>
          <button onClick={onClose} className="text-wt-text-muted hover:text-wt-text-primary text-sm">
            &times;
          </button>
        </div>

        {/* Readiness status */}
        <div className={`rounded-lg p-3 text-xs ${row.readiness === 'ready' ? 'bg-emerald-900/20 border border-emerald-800/40' : 'bg-amber-900/20 border border-amber-800/40'}`}>
          <div className="flex items-center gap-2">
            {row.readiness === 'ready' ? (
              <CheckCircle2 className="h-4 w-4 text-emerald-400" />
            ) : (
              <AlertTriangle className="h-4 w-4 text-amber-400" />
            )}
            <span className={`font-medium ${row.readiness === 'ready' ? 'text-emerald-300' : 'text-amber-300'}`}>
              {row.readiness === 'ready' ? 'Ready for Reports' : `Blocked: ${BLOCKED_REASON_LABELS[row.blocked_reason ?? ''] ?? row.blocked_reason}`}
            </span>
          </div>
        </div>

        {/* Subscription */}
        <Section title="Subscription">
          <DetailRow label="Subscribed" value={row.subscribed ? 'Yes' : 'No'} />
          {row.subscribed && <DetailRow label="Enabled" value={row.enabled ? 'Yes' : 'No'} />}
          {row.send_day_of_month != null && <DetailRow label="Send day" value={`${row.send_day_of_month} of each month`} />}
          {row.last_sent_at && <DetailRow label="Last sent" value={new Date(row.last_sent_at).toLocaleDateString()} />}
        </Section>

        {/* Telemetry */}
        <Section title="Telemetry">
          <DetailRow label="Days with data (60d)" value={String(row.days_with_data)} warn={row.days_with_data < 7} />
          <DetailRow label="Latest data" value={row.latest_telemetry_at ?? 'None'} />
        </Section>

        {/* Battery */}
        <Section title="Battery">
          <DetailRow label="Has battery" value={row.has_battery ? 'Yes' : 'No'} />
          {row.has_battery && (
            <>
              <DetailRow label="Total kWh" value={row.battery_kwh_total?.toFixed(1) ?? '—'} />
              <DetailRow label="Health snapshot" value={row.has_battery_snapshot ? 'Yes' : 'Missing'} warn={!row.has_battery_snapshot} />
              {row.last_snapshot_date && <DetailRow label="Last snapshot" value={new Date(row.last_snapshot_date).toLocaleDateString()} />}
            </>
          )}
        </Section>

        {/* Topology (multi-inverter only) */}
        {row.is_multi_inverter && (
          <Section title="Topology">
            <DetailRow label="Multi-inverter" value="Yes" />
            <DetailRow label="Confidence" value={row.topology_confidence ?? 'Unknown'} warn={row.topology_confidence === 'low'} />
            <DetailRow label="Review required" value={row.topology_review_required ? 'Yes' : 'No'} warn={row.topology_review_required} />
          </Section>
        )}

        {/* Forecast inputs */}
        <Section title="Forecast Inputs">
          <DetailRow label="Electricity rate" value={row.has_electricity_rate ? 'Configured' : 'Missing'} warn={!row.has_electricity_rate} />
        </Section>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-wt-bg-secondary rounded-lg p-3 space-y-1.5 text-xs">
      <h3 className="text-[11px] text-wt-text-muted uppercase tracking-wider font-medium">{title}</h3>
      {children}
    </div>
  );
}

function DetailRow({ label, value, warn }: { label: string; value: string; warn?: boolean }) {
  return (
    <div className="flex justify-between">
      <span className="text-wt-text-muted">{label}</span>
      <span className={`font-mono ${warn ? 'text-amber-400' : 'text-wt-text-primary'}`}>{value}</span>
    </div>
  );
}

// ─── Main tab ───────────────────────────────────────────────────────────

export function ReportReadinessTab() {
  const { data: rows, isLoading, error } = useAdminReportReadiness(true);

  const [filter, setFilter] = useState<ReadinessFilter>('subscribed');
  const [search, setSearch] = useState('');
  const [selectedRow, setSelectedRow] = useState<ReportReadinessRow | null>(null);

  const filtered = useMemo(() => {
    if (!rows) return [];
    let list = rows;
    switch (filter) {
      case 'subscribed': list = list.filter((r) => r.subscribed); break;
      case 'ready': list = list.filter((r) => r.readiness === 'ready'); break;
      case 'blocked': list = list.filter((r) => r.subscribed && r.readiness === 'blocked'); break;
      case 'not_subscribed': list = list.filter((r) => !r.subscribed); break;
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((r) => r.property_name.toLowerCase().includes(q));
    }
    return list;
  }, [rows, filter, search]);

  const counts = useMemo(() => {
    if (!rows) return { total: 0, subscribed: 0, ready: 0, blocked: 0 };
    const subscribed = rows.filter((r) => r.subscribed);
    return {
      total: rows.length,
      subscribed: subscribed.length,
      ready: subscribed.filter((r) => r.readiness === 'ready').length,
      blocked: subscribed.filter((r) => r.readiness === 'blocked').length,
    };
  }, [rows]);

  const blockedBreakdown = useMemo(() => {
    if (!rows) return new Map<string, number>();
    const map = new Map<string, number>();
    for (const r of rows) {
      if (r.subscribed && r.blocked_reason) {
        map.set(r.blocked_reason, (map.get(r.blocked_reason) ?? 0) + 1);
      }
    }
    return map;
  }, [rows]);

  return (
    <AdminTabShell isLoading={isLoading} error={error}>
      <div className="space-y-4">
        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <SummaryCard label="Total Properties" count={counts.total} className="text-wt-text-primary" />
          <SummaryCard label="Subscribed" count={counts.subscribed} className="text-blue-400" />
          <SummaryCard label="Ready" count={counts.ready} className="text-emerald-400" />
          <SummaryCard
            label="Blocked"
            count={counts.blocked}
            className={counts.blocked > 0 ? 'text-amber-400' : 'text-zinc-500'}
          />
        </div>

        {/* Blocked reason breakdown (only when there are blocked subscribed properties) */}
        {blockedBreakdown.size > 0 && (
          <div className="flex flex-wrap gap-2">
            {Array.from(blockedBreakdown.entries()).map(([reason, count]) => {
              const Icon = BLOCKED_REASON_ICONS[reason] ?? AlertTriangle;
              return (
                <Badge key={reason} variant="outline" className="gap-1 text-[10px] font-normal border-wt-border text-wt-text-muted">
                  <Icon className="h-3 w-3" />
                  {BLOCKED_REASON_LABELS[reason] ?? reason}: {count}
                </Badge>
              );
            })}
          </div>
        )}

        {/* Filters + search */}
        <div className="flex flex-wrap items-center gap-2">
          <FilterPill label="Subscribed" value="subscribed" current={filter} onSelect={setFilter} />
          <FilterPill label="Ready" value="ready" current={filter} onSelect={setFilter} />
          <FilterPill label="Blocked" value="blocked" current={filter} onSelect={setFilter} />
          <FilterPill label="Not Subscribed" value="not_subscribed" current={filter} onSelect={setFilter} />
          <FilterPill label="All" value="all" current={filter} onSelect={setFilter} />
          <div className="ml-auto relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-wt-text-muted" />
            <Input
              placeholder="Search property..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-7 pl-7 w-48 text-xs bg-wt-bg-secondary border-wt-border"
            />
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-wt-text-muted text-left border-b border-wt-border">
                <th className="pb-2 pr-3 font-medium">Property</th>
                <th className="pb-2 pr-3 font-medium">Subscribed</th>
                <th className="pb-2 pr-3 font-medium">Readiness</th>
                <th className="pb-2 pr-3 font-medium">Blocked Reason</th>
                <th className="pb-2 pr-3 font-medium text-right">Last Sent</th>
                <th className="pb-2 pr-3 font-medium text-right">Days w/ Data</th>
                <th className="pb-2 pr-3 font-medium">Topology</th>
                <th className="pb-2 pr-3 font-medium">Review</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => (
                <tr
                  key={row.property_id}
                  className="border-b border-wt-border hover:bg-wt-bg-secondary/50 cursor-pointer transition-colors"
                  onClick={() => setSelectedRow(row)}
                >
                  <td className="py-2 pr-3">
                    <span className="text-wt-text-primary font-medium truncate max-w-[200px] block">
                      {row.property_name}
                    </span>
                  </td>
                  <td className="py-2 pr-3">
                    {row.subscribed ? (
                      <span className="text-emerald-400 text-[10px]">{row.enabled ? 'Active' : 'Disabled'}</span>
                    ) : (
                      <span className="text-zinc-500 text-[10px]">No</span>
                    )}
                  </td>
                  <td className="py-2 pr-3">
                    {row.readiness === 'ready' ? (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-900/50 text-emerald-300">
                        <CheckCircle2 className="h-2.5 w-2.5" />
                        Ready
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-900/50 text-amber-300">
                        <XCircle className="h-2.5 w-2.5" />
                        Blocked
                      </span>
                    )}
                  </td>
                  <td className="py-2 pr-3 text-wt-text-muted text-[10px]">
                    {row.blocked_reason ? (BLOCKED_REASON_LABELS[row.blocked_reason] ?? row.blocked_reason) : '—'}
                  </td>
                  <td className="py-2 pr-3 text-right font-mono text-wt-text-muted">
                    {row.last_sent_at ? new Date(row.last_sent_at).toLocaleDateString() : '—'}
                  </td>
                  <td className="py-2 pr-3 text-right font-mono text-wt-text-primary">
                    {row.days_with_data}
                  </td>
                  <td className="py-2 pr-3">
                    {row.is_multi_inverter ? (
                      <span className={`text-[10px] ${row.topology_confidence === 'low' ? 'text-amber-400' : 'text-wt-text-muted'}`}>
                        {row.topology_confidence ?? '—'}
                      </span>
                    ) : (
                      <span className="text-zinc-600 text-[10px]">n/a</span>
                    )}
                  </td>
                  <td className="py-2 pr-3">
                    {row.topology_review_required ? (
                      <AlertTriangle className="h-3 w-3 text-amber-400" />
                    ) : null}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={8} className="py-8 text-center text-wt-text-muted">
                    No properties match the current filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail panel */}
      {selectedRow && (
        <ReadinessDetailPanel row={selectedRow} onClose={() => setSelectedRow(null)} />
      )}
    </AdminTabShell>
  );
}

// ─── Small components ───────────────────────────────────────────────────

function SummaryCard({ label, count, className }: { label: string; count: number; className?: string }) {
  return (
    <div className="bg-wt-bg-secondary rounded-lg p-3 border border-wt-border">
      <p className="text-[11px] text-wt-text-muted uppercase tracking-wider">{label}</p>
      <p className={`text-2xl font-mono font-semibold mt-1 ${className ?? 'text-wt-text-primary'}`}>
        {count}
      </p>
    </div>
  );
}

function FilterPill<T extends string>({
  label, value, current, onSelect,
}: { label: string; value: T; current: T; onSelect: (v: T) => void }) {
  const active = value === current;
  return (
    <button
      onClick={() => onSelect(value)}
      className={`px-2 py-0.5 rounded text-[11px] transition-colors ${
        active
          ? 'bg-wt-accent/20 text-wt-accent border border-wt-accent/40'
          : 'bg-wt-bg-tertiary text-wt-text-muted border border-transparent hover:border-wt-border'
      }`}
    >
      {label}
    </button>
  );
}
